import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RootModule } from './root/root.module';
import { environment } from '@app-environment';
import { UploadModule } from './upload/upload.module';
import { SharedModule } from './shared/shared.module';
import { Router, RouteReuseStrategy } from '@angular/router';
import { MainRoutesReuseStrategy } from './main/reuse-strategy';
import { catchError, map, take } from 'rxjs/operators';
import { AppLocaleId } from './root/root-services/app-locale-id';
import { of } from 'rxjs';
import {
    appReducer,
    AuthEffects,
    ConfigEffects,
    DocumentEffects,
    LoadingEffects,
    ProfileEffects,
    StartupEffects,
    TranslationEffects,
    UploadEffects,
    UserEffects
} from '@app-root-store';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { DownloadEffects } from './root/store/effects/download.effects';
import { FilterEffects } from './root/store/effects/filter.effects';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        RootModule,
        UploadModule,
        SharedModule,
        StoreModule.forRoot(appReducer.reducers, {
            metaReducers: [appReducer.clearFilesMetaReducer],
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true
            }
        }),
        !environment.production ? StoreDevtoolsModule.instrument() : [],
        StoreDevtoolsModule.instrument({
            maxAge: 100,
            logOnly: environment.production
        }),
        StoreRouterConnectingModule.forRoot({
            routerState: RouterState.Minimal
        }),
        EffectsModule.forRoot([
            LoadingEffects,
            UserEffects,
            AuthEffects,
            StartupEffects,
            ProfileEffects,
            FilterEffects,
            UploadEffects,
            DownloadEffects,
            ConfigEffects,
            DocumentEffects,
            TranslationEffects
        ])
    ],
    providers: [
        { provide: LOCALE_ID, useClass: AppLocaleId },
        {
            provide: RouteReuseStrategy,
            useClass: MainRoutesReuseStrategy
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService), inject(Router));
            return initializerFn();
        })
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}

function msalInstanceFactory(): PublicClientApplication {
    return new PublicClientApplication(environment.msalConfig);
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        protectedResourceMap.set(`${item.uri}/*`, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.apiConfig[0].scopes],
            state: window.location.pathname + window.location.search
        },
        loginFailedRoute: '/unauthorized'
    };
}

function initializeMsal(msalService: MsalService, router: Router) {
    return () =>
        msalService.handleRedirectObservable().pipe(
            map((authResult): undefined => {
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);

                    // Check if MSAL stored the original state (URL) and navigate back
                    if (authResult && authResult.state) {
                        const originalUrl = authResult.state;
                        if (originalUrl && originalUrl !== '/' && originalUrl !== '/main') {
                            void router.navigateByUrl(originalUrl);
                            return undefined;
                        }
                    }
                }
                return undefined;
            }),
            catchError((_error) => {
                void router.navigateByUrl('unauthorized');
                return of(undefined);
            }),
            take(1)
        );
}
